import BigNumber from 'bignumber.js';
import { AuthBase } from '../AuthBase';
import {
  KlineTick,
  idlFactory as klineIDL,
  _SERVICE as KlineActor
} from '@deland-labs/orderbook_kline_client';
import { executeWithLogging } from '../utils/errorLogger';
import { CanisterError } from '../utils/exception';
import { parseToCommon } from '../utils/helper';

export interface Tick {
  low: BigNumber;
  high: BigNumber;
  close: BigNumber;
  open: BigNumber;
  volume: BigNumber;
  time: number;
  tickIndex: number;
}

export enum TickSpaceIndex {
  OneMinute = 1,
  FiveMinutes = 2,
  FifteenMinutes = 3,
  ThirtyMinutes = 4,
  OneHour = 5,
  FourHours = 6,
  OneDay = 7,
  Week = 8,
  Month = 9
}

export class KLine extends AuthBase {
  private klineCanisterId: string;
  constructor(klineCanisterId: string) {
    super();
    this.klineCanisterId = klineCanisterId;
  }

  /**
   * fetch latest tick
   * @param tickSpace
   * @returns
   */
  public fetchCurrentTick = async (
    tickSpace: TickSpaceIndex
  ): Promise<Tick | undefined> => {
    return await executeWithLogging(async () => {
      const klineActor = await this.createKlineActor(this.klineCanisterId);
      const res = await klineActor.get_current_kline(tickSpace);
      if ('Ok' in res) {
        return res.Ok.length === 0
          ? undefined
          : this.parseOriginTickToCommon(tickSpace, res.Ok[0]);
      } else throw new CanisterError(res.Err);
    });
  };
  /**
   * fetch ticks
   * @param tickSpaceIndex
   * @param offset
   * @param limit
   * @returns
   */
  public fetchTicks = async (
    tickSpaceIndex: TickSpaceIndex,
    offset: number,
    limit: number
  ): Promise<Array<Tick>> => {
    const klineActor = await this.createKlineActor(this.klineCanisterId);
    const res = await klineActor.get_kline_ticks(tickSpaceIndex, offset, limit);
    if ('Ok' in res)
      return res.Ok.map(tick =>
        this.parseOriginTickToCommon(tickSpaceIndex, tick)
      );
    else throw new CanisterError(res.Err);
  };

  private parseOriginTickToCommon = (
    tickSpaceIndex: TickSpaceIndex,
    originTick: KlineTick
  ): Tick => {
    return {
      low: parseToCommon(originTick.low),
      high: parseToCommon(originTick.high),
      close: parseToCommon(originTick.close),
      open: parseToCommon(originTick.open),
      volume: parseToCommon(originTick.volume),
      tickIndex: parseToCommon(originTick.tick_index).toNumber(),
      time: this.parseTickIndexToTimestamp(
        tickSpaceIndex,
        originTick.tick_index
      )
    };
  };

  private parseTickIndexToTimestamp = (
    tickSpaceIndex: TickSpaceIndex,
    tickIndex: bigint
  ): number => {
    // start date is 2017-10-01
    const startDate = new Date(1506816000000);
    let tickIndexBN = new BigNumber(tickIndex.toString());
    let targetDate = startDate;
    switch (tickSpaceIndex) {
      case TickSpaceIndex.OneMinute:
        targetDate = new Date(
          startDate.getTime() + tickIndexBN.toNumber() * 60 * 1000
        );
        break;
      case TickSpaceIndex.FiveMinutes:
        targetDate = new Date(
          startDate.getTime() + tickIndexBN.toNumber() * 5 * 60 * 1000
        );
        break;
      case TickSpaceIndex.FifteenMinutes:
        targetDate = new Date(
          startDate.getTime() + tickIndexBN.toNumber() * 15 * 60 * 1000
        );
        break;
      case TickSpaceIndex.ThirtyMinutes:
        targetDate = new Date(
          startDate.getTime() + tickIndexBN.toNumber() * 30 * 60 * 1000
        );
        break;
      case TickSpaceIndex.OneHour:
        targetDate = new Date(
          startDate.getTime() + tickIndexBN.toNumber() * 60 * 60 * 1000
        );
        break;
      case TickSpaceIndex.FourHours:
        targetDate = new Date(
          startDate.getTime() + tickIndexBN.toNumber() * 60 * 60 * 4 * 1000
        );
        break;
      case TickSpaceIndex.OneDay:
        targetDate = new Date(
          startDate.getTime() + tickIndexBN.toNumber() * 60 * 60 * 24 * 1000
        );
        break;
      case TickSpaceIndex.Week:
        targetDate = new Date(
          startDate.getTime() + tickIndexBN.toNumber() * 60 * 60 * 24 * 7 * 1000
        );
        break;
      case TickSpaceIndex.Month:
        {
          let year = startDate.getFullYear();
          let month = startDate.getMonth() + tickIndexBN.toNumber();
          let day = startDate.getDate();
          if (month > 12) {
            year += month / 12;
          }
          targetDate = new Date(year, month, day);
        }
        break;
      default:
        throw new Error('unsupported tickSpaceIndex');
    }

    return targetDate.getTime();
  };

  /**
   * create kline actor
   * @param klineCanisterId  kline canister id
   * @param anonymous is anonymous access
   * @returns
   */
  private createKlineActor = (
    klineCanisterId: string,
    anonymous: boolean = true
  ) => {
    return this.createActor<KlineActor>(klineCanisterId, klineIDL, anonymous);
  };
}
